/**
 * Canny → Supercomputer generation: background poller for in-flight
 * WaveSpeed jobs tracked in the editor store (`genProposals`).
 *
 * Renders nothing — pure side effect. Must be mounted somewhere that stays
 * alive regardless of which left panel is showing (the chat sidebar itself
 * unmounts when `leftMode !== 'chat'`, see `EditorShell.tsx`), so it is
 * mounted once in `EditorShell`.
 *
 * Every ~3s it finds every job with `state === 'running'` across all
 * proposals, polls `GET /api/ai/wavespeed/status/<jobId>`, and on
 * `completed` builds an `EditorMedia` and adds it to the timeline via
 * `addClip`, then flips the job to `completed`. Provider/server `failed` is
 * terminal; the client deliberately has no shorter timeout because the
 * status API owns the 30-minute stale/refund policy for slow video models.
 */
'use client';

import { useEffect, useRef } from 'react';
import { useAuthToken } from '@/hooks/useAuthToken';
import { useEditorStore } from '@/lib/editor/editorStore';
import { applyCompletedGenJob, failCompletedGenJob } from '@/lib/editor/agent/applyCompletedGenJob';
import { isPollableGenerationJob } from '@/lib/editor/agent/genJobPollable';
import type { GenShotJob } from '@/types/editor';

// Fallback interval behind the SSE push (`useCannyGenStream`): the webhook now
// delivers completions near-instantly, so this only needs to recover events
// missed while the stream was down / the user offline.
const POLL_INTERVAL_MS = 15000;

// Re-exported for existing importers that pull it from this module.
export { isPollableGenerationJob };

type AuthedFetch = (url: string, init?: RequestInit) => Promise<Response>;

export function useGenerationPoller(): null {
  const { authedFetch } = useAuthToken();

  // authedFetch is a fresh function identity on every render (useCallback
  // deps on getToken, itself stable) — keep a ref so the interval effect
  // below doesn't need to restart on every render.
  const authedFetchRef = useRef(authedFetch);
  authedFetchRef.current = authedFetch;

  // toolCallId -> whether we've already applied the first shot's aspect
  // ratio for this proposal, so we don't re-apply it on every completion.
  const aspectAppliedRef = useRef<Set<string>>(new Set());
  // Prevents overlapping ticks if a previous tick's awaits haven't settled
  // before the next interval fires (e.g. several running jobs, slow network).
  const inFlightRef = useRef(false);

  useEffect(() => {
    const interval = window.setInterval(() => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      void pollGenerationJobsOnce(authedFetchRef.current, aspectAppliedRef.current).finally(() => {
        inFlightRef.current = false;
      });
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, []);

  return null;
}

export async function pollGenerationJobsOnce(
  authedFetch: AuthedFetch,
  aspectApplied: Set<string>
) {
  const proposals = useEditorStore.getState().genProposals;
  const runningJobs: Array<{ toolCallId: string; job: GenShotJob }> = [];
  for (const proposal of proposals) {
    for (const job of proposal.jobs) {
      if (isPollableGenerationJob(job)) {
        runningJobs.push({ toolCallId: proposal.toolCallId, job });
      }
    }
  }
  if (runningJobs.length === 0) return;

  for (const { toolCallId, job } of runningJobs) {
    if (job.state === 'error') {
      // Immediately replace the obsolete false-error in the UI. The status
      // request below will either keep it running, complete it, or surface a
      // real provider/server failure.
      useEditorStore.getState().updateGenJob(toolCallId, job.localId, {
        state: 'running',
        error: undefined,
      });
    }

    try {
      const res = await authedFetch(`/api/ai/wavespeed/status/${job.jobId}`);
      const json = await res.json();
      if (!res.ok || !json?.success || !json?.data) continue;
      const data = json.data as {
        status: string;
        outputs?: string[];
        s3Url?: string;
        videoId?: string | null;
        error?: string;
      };

      if (data.status === 'completed') {
        applyCompletedGenJob(
          job.jobId!,
          { src: data.s3Url || data.outputs?.[0], videoId: data.videoId ?? undefined },
          aspectApplied
        );
      } else if (data.status === 'failed') {
        failCompletedGenJob(job.jobId!, data.error || 'Generation failed');
      }
      // queued/processing — keep polling next tick.
    } catch {
      // Transient network error — retry next tick, don't flip to error.
    }
  }
}
